import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Modal,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/CloseRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCartRounded';
import SellIcon from '@mui/icons-material/SellRounded';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAddRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';

import { createMovementsBatch } from '../../api/movements';
import { BotMap, IManualMovement } from '../../shared/types';
import { useTradingTaskForm, TradingTaskType } from '../../hooks/useTradingTaskForm';
import { useStagedMovements } from '../../utils/useStagedMovements';
import { ReconnectingWebSocket } from '../../socket';
import { StatusPill } from '../ui/StatusPill';
import { ChainAvatar, chainName, chainOf } from '../ui/ChainAvatar';
import { botLongLabel } from '../../utils/botNames';
import { tokens } from '../../theme';

interface TradingTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message?: string) => void;
  botMap: BotMap;
  botTokens: Record<string, string[]>;
  socket: ReconnectingWebSocket;
  /** When provided, restricts the modal to only the relevant task type and pre-selects the instance */
  defaultBotType?: 'dipster' | 'dumper';
}

interface SubmissionStatus {
  total: number;
  processed: number;
  success: number;
  errors: number;
}

const modalStyle = {
  position: 'absolute' as const,
  // Phones: bottom sheet; larger screens: centered card.
  top: { xs: 'auto', sm: '50%' },
  bottom: { xs: 0, sm: 'auto' },
  left: { xs: 0, sm: '50%' },
  transform: { xs: 'none', sm: 'translate(-50%, -50%)' },
  width: { xs: '100%', sm: 1100 },
  maxWidth: { sm: 'calc(100vw - 32px)' },
  // Glass chrome shared with the themed dialogs.
  bgcolor: 'rgba(18,18,20,0.92)',
  backdropFilter: 'blur(24px) saturate(150%)',
  WebkitBackdropFilter: 'blur(24px) saturate(150%)',
  borderRadius: { xs: '28px 28px 0 0', sm: '28px' },
  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.1), 0 40px 90px -20px ${alpha('#000000', 0.9)}`,
  border: `1px solid ${alpha('#FFFFFF', 0.14)}`,
  borderBottom: { xs: 0, sm: `1px solid ${alpha('#FFFFFF', 0.14)}` },
  display: 'flex',
  flexDirection: 'column',
  maxHeight: { xs: 'calc(100dvh - env(safe-area-inset-top) - 12px)', sm: '90vh' },
  pb: { xs: 'env(safe-area-inset-bottom)', sm: 0 },
  overflow: 'hidden',
};

const TASK_TYPES: Record<TradingTaskType, { tab: string; title: string; Icon: typeof SellIcon }> = {
  MANUAL_PREMIUM: { tab: 'Add premium (trigger buys)', title: 'Add premium · trigger buys', Icon: AddShoppingCartIcon },
  RIF_SELL: { tab: 'RIF sell (trigger sales)', title: 'RIF sell · trigger sales', Icon: SellIcon },
};

const INSTANCE_LABEL: Record<'dipster' | 'dumper', string> = { dipster: 'Dipster', dumper: 'Dumper' };
/** Generic instance bot ids, shown by instance name instead of in capitals. */
const GENERIC_BOT_LABEL: Record<string, string> = { DIPSTER: 'Dipster', DUMPER: 'Dumper' };

/** "1 task", "3 tasks", "0 tasks". */
const pluralize = (count: number, noun: string) => `${count} ${count === 1 ? noun : `${noun}s`}`;

/**
 * After a task is staged, Execute ignores taps for this long: on phones "Stage task" turns into
 * "Execute (n)" in the same spot, so a double tap must not send the batch unreviewed.
 */
const EXECUTE_GRACE_MS = 400;

/** Notes, not states: neutral grey (blue is the Dipster's identity, never an info colour). */
const noteSx = {
  bgcolor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.10)',
  color: 'text.primary',
  '& .MuiAlert-icon': { color: 'text.secondary' },
} as const;

/** Chain group title, as in the sidebar: logo + small tracked caps. */
const chainHeaderSx = {
  fontSize: '0.75rem',
  fontWeight: 650,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.6)',
} as const;

const TradingTaskModal: React.FC<TradingTaskModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  botMap,
  botTokens,
  socket,
  defaultBotType,
}) => {
  const theme = useTheme();
  // Phones: the sticky footer carries the next step ("Stage task" until something is queued).
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const { stagedMovements, addMovement, removeMovement, clearMovements } = useStagedMovements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBots, setSelectedBots] = useState<string[]>([]);
  const [assignToNoBot, setAssignToNoBot] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus | null>(null);
  /** Scrolling content area and the review-queue column inside it. */
  const contentRef = useRef<HTMLDivElement>(null);
  const queueRef = useRef<HTMLDivElement>(null);
  /** Execute ignores taps until this time (see EXECUTE_GRACE_MS). */
  const executeLockedUntil = useRef(0);

  const {
    taskType,
    setTaskType,
    token,
    setToken,
    quantity,
    setQuantity,
    premiumAmount,
    setPremiumAmount,
    premiumCurrency,
    setPremiumCurrency,
    premiumPrice,
    setPremiumPrice,
    activationPrice,
    setActivationPrice,
    limitPrice,
    setLimitPrice,
    targetInstance,
    setTargetInstance,
    isPremium,
    isSmartSell,
    availableTokens,
    resetForm,
  } = useTradingTaskForm({ botTokens });

  const date = useMemo(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const progressLabel = submissionStatus
    ? `Processing ${submissionStatus.processed} of ${pluralize(submissionStatus.total, 'task')}…`
    : `Submitting ${pluralize(stagedMovements.length, 'task')}…`;

  const instanceLabel = INSTANCE_LABEL[targetInstance];
  const TaskTypeIcon = TASK_TYPES[taskType].Icon;
  const botName = (botId: string) => botMap[botId]?.name ?? GENERIC_BOT_LABEL[botId] ?? botId;

  /** Bots a premium can be attributed to (not the Ethereum ones), by chain: [chain, ids][]. */
  const botGroups = useMemo(() => {
    const byChain: Record<string, string[]> = {};
    Object.entries(botMap)
      .filter(([, details]) => details.chain !== 'ETH')
      .forEach(([id]) => {
        const chain = chainOf(id);
        (byChain[chain] = byChain[chain] ?? []).push(id);
      });
    return Object.keys(byChain)
      .sort()
      .map(chain => [chain, byChain[chain].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))] as const);
  }, [botMap]);

  const handleRequestClose = (_event?: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (isSubmitting) return;
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      clearMovements();
      resetForm();
      setSelectedBots([]);
      setAssignToNoBot(false);
      setIsSubmitting(false);
      setSubmissionStatus(null);
      setSubmitError(null);
      setTaskType('MANUAL_PREMIUM');
    } else if (defaultBotType) {
      // Pre-select instance and task type based on which bot opened the modal
      setTargetInstance(defaultBotType);
      setTaskType(defaultBotType === 'dumper' ? 'RIF_SELL' : 'MANUAL_PREMIUM');
    }
  }, [isOpen, clearMovements, resetForm, setTaskType, defaultBotType, setTargetInstance]);

  useEffect(() => {
    const handleProgress = (payload: SubmissionStatus & { message?: string }) => {
      setSubmissionStatus(payload);
      setSubmitError(null);
    };

    const handleCompleted = (payload: { message?: string; total: number; success: number }) => {
      const message = payload?.message || `Processed ${payload.success} of ${pluralize(payload.total, 'task')} successfully`;
      setIsSubmitting(false);
      setSubmissionStatus(null);
      clearMovements();
      resetForm();
      setSelectedBots([]);
      setAssignToNoBot(false);
      setTaskType('MANUAL_PREMIUM');
      onSuccess?.(message);
      onClose();
    };

    const handleError = (payload: any) => {
      const message = payload?.error || 'Failed to process tasks batch.';
      setSubmitError(message);
      setIsSubmitting(false);
      setSubmissionStatus(null);
    };

    socket.on('movementBatchProgress', handleProgress);
    socket.on('movementBatchCompleted', handleCompleted);
    socket.on('movementBatchError', handleError);

    return () => {
      socket.off('movementBatchProgress', handleProgress);
      socket.off('movementBatchCompleted', handleCompleted);
      socket.off('movementBatchError', handleError);
    };
  }, [socket, clearMovements, onClose, onSuccess, resetForm, setTaskType]);

  const handleToggleBot = (botId: string) => {
    if (isSubmitting) return;
    setSelectedBots(prev =>
      prev.includes(botId) ? prev.filter(id => id !== botId) : [...prev, botId]
    );
  };

  const handleAddToStage = () => {
    if (isSmartSell) {
      const q = parseFloat(quantity);
      if (isNaN(q) || !token || !activationPrice || !limitPrice) return;

      const genericBotId = targetInstance.toUpperCase();

      const newMovement: IManualMovement = {
        id: `${genericBotId}-${Date.now()}-${Math.random()}`,
        botId: genericBotId,
        movementType: 'RIF_SELL',
        date,
        token,
        quantity: q,
        amountUSD: 0,
        targetInstance,
        status: 'ACTIVE',
        smartSellConfig: {
          activationPrice: parseFloat(activationPrice),
          limitPrice: parseFloat(limitPrice),
        }
      };
      addMovement(newMovement);
    } else if (isPremium) {
      const total = Number(premiumAmount) || 0;
      if (total <= 0) return;

      if (assignToNoBot) {
        const newMovement: IManualMovement = {
          id: `NO_BOT-${Date.now()}-${Math.random()}`,
          botId: 'NO_BOT',
          movementType: 'MANUAL_PREMIUM',
          date,
          amountUSD: total * premiumPrice,
          token: premiumCurrency,
          quantity: total,
          price: premiumPrice,
          targetInstance,
        };
        addMovement(newMovement);
      } else if (selectedBots.length === 0) {
        const genericBotId = targetInstance.toUpperCase();
        const newMovement: IManualMovement = {
          id: `${genericBotId}-${Date.now()}-${Math.random()}`,
          botId: genericBotId,
          movementType: 'MANUAL_PREMIUM',
          date,
          amountUSD: total * premiumPrice,
          token: premiumCurrency,
          quantity: total,
          price: premiumPrice,
          targetInstance,
        };
        addMovement(newMovement);
      } else {
        const premiumPerBot = total / selectedBots.length;
        selectedBots.forEach(botId => {
          const newMovement: IManualMovement = {
            id: `${botId}-${Date.now()}-${Math.random()}`,
            botId,
            movementType: 'MANUAL_PREMIUM',
            date,
            amountUSD: premiumPerBot * premiumPrice,
            token: premiumCurrency,
            quantity: premiumPerBot,
            price: premiumPrice,
            targetInstance,
          };
          addMovement(newMovement);
        });
      }
    }

    executeLockedUntil.current = Date.now() + EXECUTE_GRACE_MS;
    resetForm();
    setSelectedBots([]);
    setAssignToNoBot(false);
  };

  /**
   * Sends the staged batch. Only the Execute button calls it: nothing in this modal is a submit
   * button or a form (a footer button that changed type mid-tap once sent a batch unreviewed).
   */
  const handleExecute = () => {
    if (isSubmitting || stagedMovements.length === 0) return;
    // The second tap of a double tap on "Stage task", landing on the Execute that replaced it.
    if (Date.now() < executeLockedUntil.current) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmissionStatus({
      total: stagedMovements.length,
      processed: 0,
      success: 0,
      errors: 0,
    });

    createMovementsBatch(socket, stagedMovements);
  };

  const canAddToStage = isPremium
    ? Number(premiumAmount) > 0
    : isSmartSell
    ? Boolean(token && quantity && activationPrice && limitPrice)
    : false;

  // On phones, while nothing is queued, the footer button stages the task (and the in-body one is hidden).
  const footerStages = isPhone && stagedMovements.length === 0;

  // Show what was just staged: stacked layouts (phones) keep the review queue below the form, off
  // screen. Side by side it is already in view and nothing moves.
  const stagedCount = stagedMovements.length;
  const lastStagedCount = useRef(stagedCount);
  useEffect(() => {
    const added = stagedCount > lastStagedCount.current;
    lastStagedCount.current = stagedCount;
    const content = contentRef.current;
    const queue = queueRef.current;
    if (!added || !content || !queue) return;
    const view = content.getBoundingClientRect();
    const panel = queue.getBoundingClientRect();
    if (panel.top >= view.top && panel.bottom <= view.bottom) return;
    // "Step 2 · Review queue" to the top edge; a queue taller than the view shows its end (the new task).
    const delta = panel.height > view.height - 16 ? panel.bottom - view.bottom + 8 : panel.top - view.top - 8;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    content.scrollTo?.({ top: content.scrollTop + delta, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [stagedCount]);

  return (
    <Modal open={isOpen} onClose={handleRequestClose} closeAfterTransition>
      <>
        <Box sx={modalStyle}>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="h6" component="h2" fontWeight={700}>
                  Trading tasks
                </Typography>
                {/* Identity, not status: neutral pill with the instance's colour dot. */}
                <StatusPill
                  tone="neutral"
                  size="sm"
                  label={instanceLabel}
                  icon={
                    <Box
                      component="span"
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        flexShrink: 0,
                        bgcolor: targetInstance === 'dipster' ? tokens.chart.green : tokens.chart.violet,
                      }}
                    />
                  }
                />
              </Stack>
              <IconButton onClick={handleRequestClose} disabled={isSubmitting} aria-label="Close" sx={{ width: 44, height: 44, mr: -1 }}>
                <CloseIcon />
              </IconButton>
            </Stack>

            {/* Instance Selector — hidden when modal is opened from a specific bot */}
            {!defaultBotType && (
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Typography variant="body2" color="text.secondary">Target instance</Typography>
                <ToggleButtonGroup
                  value={targetInstance}
                  exclusive
                  onChange={(_, val) => val && setTargetInstance(val)}
                  size="small"
                >
                  <ToggleButton value="dipster" sx={{ px: 2 }}>Dipster</ToggleButton>
                  <ToggleButton value="dumper" sx={{ px: 2 }}>Dumper</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            )}

            {/* Task type: a segmented control only when both types are available */}
            {defaultBotType ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <TaskTypeIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography component="h3" sx={{ fontSize: '0.9375rem', fontWeight: 650 }}>
                  {TASK_TYPES[taskType].title}
                </Typography>
              </Stack>
            ) : (
              <Tabs
                value={taskType}
                onChange={(_, value) => value && setTaskType(value as TradingTaskType)}
                variant="fullWidth"
                TabIndicatorProps={{
                  sx: {
                    top: 0,
                    height: '100%',
                    bgcolor: 'rgba(255,255,255,0.13)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.14)',
                    zIndex: 0,
                    borderRadius: 999
                  }
                }}
                sx={{
                  p: '4px',
                  minHeight: 0,
                  bgcolor: alpha(theme.palette.common.black, 0.35),
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 999,
                  '& .MuiTab-root': {
                    zIndex: 1,
                    minHeight: 40,
                    borderRadius: 999,
                    color: 'text.secondary',
                    fontWeight: 650,
                    '&.Mui-selected': { color: 'text.primary' }
                  }
                }}
              >
                {(Object.keys(TASK_TYPES) as TradingTaskType[]).map(type => {
                  const { Icon, tab } = TASK_TYPES[type];
                  return <Tab key={type} icon={<Icon />} iconPosition="start" label={tab} value={type} />;
                })}
              </Tabs>
            )}
          </Box>

          {/* Content: sized to what it holds (the sheet/card caps at 90vh and this part scrolls). Side by
              side, both columns share the row's height, so the review queue ends where the form ends. */}
          <Grid
            ref={contentRef}
            container
            columnSpacing={4}
            rowSpacing={3}
            sx={{ p: 2, flex: '0 1 auto', minHeight: 0, overflowY: 'auto', alignItems: 'stretch' }}
          >
            {/* Left Panel - Form: the wider column (7/12), so three bot names fit per row; the queue
                beside it lists short rows. */}
            <Grid size={{ xs: 12, md: 7 }} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 2 }}>
                Step 1 · Configure {isPremium ? 'premium' : 'smart sell'}
              </Typography>

              <Stack spacing={1.5} mt={1.5}>
                {isPremium ? (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 2,
                        // Narrow currency/price columns leave the amount's helper on one line.
                        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'minmax(0, 1fr) 104px 104px' },
                      }}
                    >
                      <TextField
                        label="Total premium amount"
                        type="number"
                        inputProps={{ inputMode: "decimal" }}
                        value={premiumAmount}
                        onChange={e => {
                          const v = e.target.value;
                          setPremiumAmount(v === '' ? '' : parseFloat(v));
                        }}
                        sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                        disabled={isSubmitting}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        helperText="Amount will be distributed among selected bots"
                      />
                      <TextField
                        select
                        label="Currency"
                        value={premiumCurrency}
                        onChange={e => setPremiumCurrency(e.target.value)}
                        disabled={isSubmitting}
                      >
                        {['BTC', 'USDT', 'USDC'].map(c => (
                          <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Price"
                        type="number"
                        inputProps={{ inputMode: "decimal" }}
                        value={premiumPrice}
                        onChange={e => setPremiumPrice(parseFloat(e.target.value) || 0)}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        disabled={isSubmitting}
                      />
                    </Box>
                    <Alert severity="info" sx={noteSx}>
                      This will create DCA buy tasks for the Dipster to purchase RIF equivalent to the premium amount.
                    </Alert>
                    <FormControlLabel
                      // -9px cancels the checkbox's 9px padding, so the box lines up with the fields' edge.
                      // Doubled class (&&): the Stack resets its children's margins with a stronger selector.
                      sx={{ '&&': { ml: '-9px' } }}
                      control={
                        <Checkbox
                          checked={assignToNoBot}
                          onChange={e => {
                            setAssignToNoBot(e.target.checked);
                            if (e.target.checked) setSelectedBots([]);
                          }}
                          disabled={isSubmitting}
                        />
                      }
                      label="Assign to NO_BOT (do not count in metrics)"
                    />
                  </>
                ) : (
                  <>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        select
                        label="Token to Sell"
                        value={token}
                        onChange={e => setToken(e.target.value)}
                        fullWidth
                        disabled={isSubmitting}
                      >
                        {(availableTokens.length > 0 ? availableTokens : ['RIF', 'RDOC', 'USDT']).map(t => (
                          <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        label="Total amount"
                        type="number"
                        inputProps={{ inputMode: "decimal" }}
                        value={quantity}
                        onChange={e => setQuantity(e.target.value)}
                        fullWidth
                        disabled={isSubmitting}
                        InputProps={{
                          endAdornment: <Typography variant="caption" sx={{ ml: 1 }}>{token}</Typography>
                        }}
                        helperText="Will be distributed among selected bots"
                      />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="Activation price"
                        type="number"
                        inputProps={{ inputMode: "decimal" }}
                        value={activationPrice}
                        onChange={e => setActivationPrice(e.target.value)}
                        fullWidth
                        helperText="Start selling above this price"
                        disabled={isSubmitting}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                      <TextField
                        // The task's limit: "Min price" here and on the Dumper card ("Bot floor" is the bot's).
                        label="Min price"
                        type="number"
                        inputProps={{ inputMode: "decimal" }}
                        value={limitPrice}
                        onChange={e => setLimitPrice(e.target.value)}
                        fullWidth
                        helperText="Stop selling below this price"
                        disabled={isSubmitting}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    </Stack>

                    <Alert severity="info" sx={noteSx}>
                      Smart Sell tasks will trigger automatic sales when the activation price is reached.
                    </Alert>
                  </>
                )}

                {/* Bot Selection - Only for Premium */}
                {isPremium && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" mb={1}>
                      Select bots ({selectedBots.length} selected) · optional
                    </Typography>
                    {assignToNoBot ? (
                      <Alert severity="info" sx={{ ...noteSx, mb: 1 }}>
                        Task will run on <strong>{instanceLabel}</strong> but be attributed to <strong>NO_BOT</strong>.
                      </Alert>
                    ) : selectedBots.length === 0 && (
                      // Selection is optional: a note, not a warning.
                      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: -0.5, mb: 1 }}>
                        None selected: the premium goes to <strong>{instanceLabel}</strong> (generic).
                      </Typography>
                    )}
                    {/* Natural height: every bot chip stays visible (the content area scrolls if needed).
                        Chips by chain, as in the sidebar ("USDC / WETH · Bot 1" runs on several chains);
                        as many columns as fit a whole name (one on phones), 44px tall and 8px apart on touch. */}
                    <Paper variant="outlined" sx={{ p: 1, opacity: assignToNoBot ? 0.5 : 1, transition: 'opacity 0.2s ease' }}>
                      <Stack spacing={1.5}>
                        {botGroups.map(([chain, ids]) => (
                          <Box key={chain} component="section" aria-label={chainName(chain)}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.25, pt: 0.5, mb: 0.75 }}>
                              <ChainAvatar chain={chain} size={18} />
                              <Typography sx={chainHeaderSx}>{chainName(chain)}</Typography>
                            </Stack>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(184px, 1fr))',
                                gap: 0.5,
                                '@media (pointer: coarse)': { gap: 1 },
                              }}
                            >
                              {ids.map(id => {
                                const isSelected = selectedBots.includes(id);
                                return (
                                  <Card
                                    key={id}
                                    variant="outlined"
                                    sx={{
                                      borderColor: isSelected ? 'secondary.main' : 'divider',
                                      bgcolor: isSelected ? alpha(theme.palette.secondary.main, 0.08) : undefined,
                                      transition: 'border-color 0.2s ease, background-color 0.2s ease',
                                    }}
                                  >
                                    <CardActionArea
                                      onClick={() => handleToggleBot(id)}
                                      disabled={isSubmitting || assignToNoBot}
                                      aria-pressed={isSelected}
                                      title={id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        gap: 0.75,
                                        minHeight: 36,
                                        px: 1.25,
                                        py: 0.75,
                                        '@media (pointer: coarse)': { minHeight: 44 },
                                      }}
                                    >
                                      <ChainAvatar chain={chainOf(id)} size={16} />
                                      <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{ flex: 1, minWidth: 0, fontWeight: 600, color: isSelected ? 'text.primary' : 'text.secondary' }}
                                      >
                                        {botLongLabel(id)}
                                      </Typography>
                                      {isSelected && <CheckCircleIcon color="secondary" sx={{ fontSize: 16, flexShrink: 0 }} />}
                                    </CardActionArea>
                                  </Card>
                                );
                              })}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  </Box>
                )}
              </Stack>

              {/* Phones with an empty queue stage from the sticky footer instead. */}
              {!footerStages && (
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  disabled={!canAddToStage || isSubmitting}
                  onClick={handleAddToStage}
                  startIcon={<PlaylistAddIcon />}
                  sx={{ mt: 2 }}
                >
                  Stage task
                </Button>
              )}
            </Grid>

            {/* Right Panel - Staged Tasks */}
            <Grid ref={queueRef} size={{ xs: 12, md: 5 }} sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch' }}>
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 2 }}>
                Step 2 · Review queue ({stagedMovements.length})
              </Typography>
              {/* Fills the column (at least 200px), so it ends on the same line as the form; the empty
                  state is centred in it and staged tasks list from the top. */}
              <Box sx={{
                mt: 1,
                flex: '1 1 auto',
                alignSelf: 'stretch',
                minHeight: 200,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: alpha(theme.palette.common.black, 0.2),
                borderRadius: 2,
                p: 1,
              }}>
                {stagedMovements.length === 0 ? (
                  <Stack justifyContent="center" alignItems="center" sx={{ flex: 1, color: 'text.disabled', p: 2, textAlign: 'center' }}>
                    <Typography>No tasks staged.</Typography>
                    <Typography variant="caption">Stage a task to add it here.</Typography>
                  </Stack>
                ) : (
                  <List dense>
                    {stagedMovements.map(m => (
                      <ListItem
                        key={m.id}
                        secondaryAction={
                          <IconButton edge="end" size="small" onClick={() => removeMovement(m.id)} disabled={isSubmitting}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                        sx={{
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          mb: 1,
                          '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.5) }
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontWeight: 500, noWrap: true }}
                          secondaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          primary={
                            <Stack direction="row" alignItems="center" spacing={1}>
                              {/* Task type, not a status: neutral pill */}
                              <StatusPill
                                tone="neutral"
                                size="sm"
                                dot={false}
                                label={m.movementType === 'MANUAL_PREMIUM' ? 'Premium' : 'RIF sell'}
                              />
                              <Typography variant="body2" fontWeight={500} noWrap>
                                {botName(m.botId)}
                              </Typography>
                            </Stack>
                          }
                          secondary={
                            m.movementType === 'MANUAL_PREMIUM'
                              ? `${Number(m.quantity).toLocaleString()} ${m.token} @ $${Number(m.price).toLocaleString()}`
                              : `${Number(m.quantity).toLocaleString()} ${m.token} · Activation $${m.smartSellConfig?.activationPrice} · Min price $${m.smartSellConfig?.limitPrice}`
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Footer */}
          <Box sx={{ mt: 'auto', flexShrink: 0, p: 2, borderTop: 1, borderColor: 'divider' }}>
            {submitError && (
              <Alert severity="error" onClose={() => setSubmitError(null)} sx={{ mb: 1.5 }}>
                {submitError}
              </Alert>
            )}
            {/* Phones: one full-width button that is always the next step ("Stage task" until something is
                queued, then "Execute (n)"). Larger screens: helper beside the Execute button. */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="flex-end"
              spacing={{ xs: 1, sm: 2 }}
            >
              {stagedMovements.length === 0 && !isSubmitting && !isPhone && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                  Stage at least one task
                </Typography>
              )}
              {/* Two different buttons (keys), both type="button" with their own handler: React must not
                  reuse the tapped "Stage task" as "Execute", or the same tap would run the batch. */}
              {footerStages ? (
                <Button
                  key="stage"
                  type="button"
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={!canAddToStage || isSubmitting}
                  onClick={handleAddToStage}
                  startIcon={<PlaylistAddIcon />}
                >
                  Stage task
                </Button>
              ) : (
                <Button
                  key="execute"
                  type="button"
                  variant="contained"
                  color="secondary"
                  size="large"
                  disabled={isSubmitting || stagedMovements.length === 0}
                  onClick={handleExecute}
                  sx={{ minWidth: { sm: 260 } }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : stagedMovements.length === 0 ? (
                    'Execute'
                  ) : isPhone ? (
                    `Execute (${stagedMovements.length})`
                  ) : (
                    `Execute ${pluralize(stagedMovements.length, 'task')}`
                  )}
                </Button>
              )}
            </Stack>
          </Box>
        </Box>

        {/* Loading Overlay */}
        <Backdrop open={isSubmitting} sx={{ color: '#fff', zIndex: theme.zIndex.modal + 1 }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress color="inherit" />
            <Typography variant="body2">{progressLabel}</Typography>
          </Stack>
        </Backdrop>

      </>
    </Modal>
  );
};

export default TradingTaskModal;
